import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Button, Input, Textarea, Select, Label, Modal, ProgressBar, Spinner, StatusBadge, EmptyState } from "@/components/UI";
import { Plus, FolderKanban, Calendar, CheckCircle2, Pencil, Trash2, Image as ImageIcon } from "lucide-react";

const emptyForm = { name: "", description: "", status: "active", start_date: "", end_date: "", color: "#D1D5DB" };

const PROJECT_STATUS_LABEL = {
  planned: "Planned",
  active: "Active",
  on_hold: "On Hold",
  completed: "Completed",
  cancelled: "Cancelled",
};

export default function Projects() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const isEdit = editingId !== null;

  const load = () => api.get("/projects").then((r) => setProjects(r.data.filter((p) => p.status !== "completed")));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setLogoFile(null);
    setLogoPreview("");
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name || "",
      description: p.description || "",
      status: p.status || "active",
      start_date: p.start_date ? p.start_date.slice(0, 10) : "",
      end_date: p.end_date ? p.end_date.slice(0, 10) : "",
      color: p.color || "#D1D5DB",
    });
    setLogoFile(null);
    setLogoPreview(p.logo_url || "");
    setOpen(true);
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);
    setLogoPreview(file ? URL.createObjectURL(file) : "");
  };

  const save = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (logoFile) fd.append("logo", logoFile);

      if (isEdit) {
        fd.append("_method", "PUT");
        await api.post(`/projects/${editingId}`, fd);
        toast.success("Project diperbarui");
      } else {
        await api.post("/projects", fd);
        toast.success("Project dibuat");
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      setLogoFile(null);
      setLogoPreview("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/projects/${deleteTarget.id}`);
      toast.success("Project dihapus");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menghapus project");
      setDeleteTarget(null);
    }
  };

  if (!projects) return <Spinner />;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-sm text-slate-400">Timeline & progress setiap project. Project selesai otomatis masuk ke menu Arsip.</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} data-testid="create-project-button"><Plus size={16} /> Project Baru</Button>
        )}
      </div>

      {projects.length === 0 ? (
        <EmptyState icon={FolderKanban} title="Belum ada project" subtitle="Buat project pertama Anda." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer p-5 transition-colors hover:border-slate-500/40"
              onClick={() => navigate(`/projects/${p.id}`)}
              data-testid={`project-card-${p.id}`}
            >
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt={p.name} className="h-9 w-9 rounded-full border border-slate-700 object-cover" />
                  ) : (
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: p.color }} />
                  )}
                  <h3 className="font-heading text-lg font-semibold text-slate-100">{p.name}</h3>
                </div>
                <div className="flex items-center gap-1.5">
                  {p.is_late && p.status === "active" ? (
                    <span className="rounded-md bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-400">Terlambat</span>
                  ) : (
                    <StatusBadge status={p.status === "completed" ? "done" : p.status === "on_hold" ? "on_hold" : p.status === "planned" ? "planned" : p.status === "cancelled" ? "replaced" : "in_progress"} />
                  )}
                  {isAdmin && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                        className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-500/10 hover:text-slate-400"
                        data-testid={`edit-project-button-${p.id}`}
                        title="Edit project"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
                        className="rounded-lg p-1.5 text-slate-500 hover:bg-rose-500/10 hover:text-rose-400"
                        data-testid={`delete-project-button-${p.id}`}
                        title="Hapus project"
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {p.description && <p className="mb-3 line-clamp-2 text-sm text-slate-400">{p.description}</p>}

              <div className="mb-2 flex justify-between text-xs">
                <span className="text-slate-400">Progress (bobot)</span>
                <span className="font-mono tabular-nums text-slate-400">{p.progress}%</span>
              </div>
              <ProgressBar value={p.progress} max={100} color={p.color} height={8} />

              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                <span className="flex items-center gap-1"><CheckCircle2 size={13} /> {p.done_tasks}/{p.total_tasks} task</span>
                <span className="flex items-center gap-1 text-blue-400"><Calendar size={13} />
                  {p.start_date ? new Date(p.start_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) : "—"} – {p.end_date ? new Date(p.end_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) : "—"}
                </span>
                <span className="font-mono text-slate-400">{p.done_weight}/{p.total_weight} pts</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={isEdit ? "Edit Project" : "Buat Project Baru"}
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
          <Button onClick={save} disabled={saving || !form.name} data-testid="project-save">{saving ? "..." : isEdit ? "Simpan" : "Buat"}</Button></>}>
        <div className="space-y-4">
          <div><Label>Nama Project</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-blue-400">Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="project-start-date" /></div>
            <div><Label className="text-blue-400">End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="project-end-date" /></div>
          </div>
          {isEdit && (
            <div>
              <Label>Status</Label>
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="project-status">
                {Object.entries(PROJECT_STATUS_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </Select>
            </div>
          )}
          <div>
            <Label>Logo / Foto Project</Label>
            <div className="flex items-center gap-3">
              {logoPreview ? (
                <img src={logoPreview} alt="logo" className="h-12 w-12 rounded-full border border-slate-700 object-cover" />
              ) : (
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-slate-700 text-slate-600">
                  <ImageIcon size={18} />
                </span>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={onLogoChange}
                className="text-xs text-slate-400 file:mr-3 file:rounded-md file:border-0 file:bg-[#F59E0B] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-[#0F172A] hover:file:bg-[#FBBF24]"
                data-testid="project-logo"
              />
            </div>
          </div>
          <div><Label>Warna</Label><input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-20 rounded border border-slate-700 bg-slate-950" data-testid="project-color" /></div>
        </div>
      </Modal>

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Hapus Project"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Batal</Button>
            <Button variant="danger" onClick={remove} data-testid="confirm-delete-project">Hapus</Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          Yakin ingin menghapus project <strong>{deleteTarget?.name}</strong>?
          {deleteTarget?.total_tasks > 0 && (
            <span className="block text-rose-400">Project ini masih memiliki {deleteTarget.total_tasks} task.</span>
          )}
        </p>
      </Modal>
    </div>
  );
}
